import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CurrencyDollar } from "@phosphor-icons/react";

export default function Settings() {
    const { user } = useAuth();
    const [fx, setFx] = useState({ gbp_per_usd: 0.79, zwl_per_usd: 25000 });
    const [savingFx, setSavingFx] = useState(false);

    useEffect(() => {
        api.get("/settings/fx").then((r) => setFx(r.data));
    }, []);

    const saveFx = async (e) => {
        e.preventDefault();
        setSavingFx(true);
        try {
            const { data } = await api.put("/settings/fx", {
                gbp_per_usd: Number(fx.gbp_per_usd),
                zwl_per_usd: Number(fx.zwl_per_usd),
            });
            setFx(data);
            toast.success("FX rates saved");
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed");
        } finally {
            setSavingFx(false);
        }
    };

    if (user?.role !== "owner") {
        return (
            <div className="p-10" data-testid="settings-locked">
                <h1 className="font-heading text-3xl font-bold">Settings</h1>
                <p className="text-muted2 text-sm mt-2">Only the group owner can access settings.</p>
            </div>
        );
    }

    return (
        <div className="p-8 lg:p-10 max-w-[900px]" data-testid="settings-page">
            <div className="mb-8">
                <div className="overline mb-2">Configuration</div>
                <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter leading-none">SETTINGS</h1>
                <p className="text-sm text-muted2 mt-2 max-w-xl">
                    FX rates only — used to normalise multi-currency report figures to USD on the dashboard.
                </p>
            </div>

            <form onSubmit={saveFx} className="grid-card p-6 max-w-lg" data-testid="fx-form">
                <div className="flex items-center gap-2 mb-4">
                    <CurrencyDollar size={20} />
                    <div className="overline">FX rates · manual</div>
                </div>
                <p className="text-xs text-muted2 mb-4">
                    Set your conversion rates. The Executive Dashboard sums revenue and expenses in USD using these.
                </p>
                <div className="space-y-3">
                    <div>
                        <label className="overline block mb-1.5">GBP per 1 USD</label>
                        <input
                            data-testid="fx-gbp"
                            type="number" step="0.0001" required min="0.0001"
                            value={fx.gbp_per_usd}
                            onChange={(e) => setFx({ ...fx, gbp_per_usd: e.target.value })}
                            className="input-base"
                        />
                        <div className="text-xs text-muted2 mt-1 data-num">£1 = ${(1 / Number(fx.gbp_per_usd || 1)).toFixed(4)} USD</div>
                    </div>
                    <div>
                        <label className="overline block mb-1.5">ZWL per 1 USD</label>
                        <input
                            data-testid="fx-zwl"
                            type="number" step="0.01" required min="0.01"
                            value={fx.zwl_per_usd}
                            onChange={(e) => setFx({ ...fx, zwl_per_usd: e.target.value })}
                            className="input-base"
                        />
                    </div>
                    <button type="submit" disabled={savingFx} data-testid="fx-save" className="btn-primary w-full">
                        {savingFx ? "Saving…" : "Save FX rates"}
                    </button>
                    {fx.updated_at ? (
                        <div className="text-xs text-muted2">Last updated {new Date(fx.updated_at).toLocaleString()}</div>
                    ) : null}
                </div>
            </form>
        </div>
    );
}
